import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import messages from './messages';

/**
 * 다국어 (한국어 / 영어)
 * - 컴포넌트: const { t, lang, setLang } = useI18n();  t('common.save'), t('bracket.matchNo', { no: 3 })
 * - 컴포넌트 밖(utils 등): translate('common.save') — 현재 언어로 번역한다.
 * - 메시지는 src/i18n/messages/<영역>.js 에 { ko: {...}, en: {...} } 로 둔다.
 *   값은 문자열('{name}' 치환) 또는 함수(params => 문자열, 영어 복수형 등)
 * - 언어는 ?lang=en 주소 → 저장된 선택 → 한국어 순으로 정한다.
 */

export const LANGUAGES = [
  { code: 'ko', label: '한국어', shortLabel: 'KO' },
  { code: 'en', label: 'English', shortLabel: 'EN' },
];

const DEFAULT_LANGUAGE = 'ko';
const STORAGE_KEY = 'tournament-lang';
const LOCALES = { ko: 'ko-KR', en: 'en-US' };

function isSupported(code) {
  return LANGUAGES.some((language) => language.code === code);
}

function detectLanguage() {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('lang');
    if (isSupported(fromQuery)) {
      localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch {
    // 저장소를 쓸 수 없는 환경
  }
  return DEFAULT_LANGUAGE;
}

let currentLanguage = detectLanguage();

export function getLanguage() {
  return currentLanguage;
}

/** toLocaleDateString 등에 넘길 로케일 (ko-KR / en-US) */
export function getLocale() {
  return LOCALES[currentLanguage] ?? LOCALES[DEFAULT_LANGUAGE];
}

function lookup(table, key) {
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), table);
}

function interpolate(text, params) {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) => (params[name] == null ? match : String(params[name])));
}

export function translate(key, params) {
  let value = lookup(messages[currentLanguage], key);
  if (value == null) value = lookup(messages[DEFAULT_LANGUAGE], key);
  if (value == null) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing message: ${key}`);
    }
    return key;
  }
  if (typeof value === 'function') return value(params ?? {});
  return typeof value === 'string' ? interpolate(value, params) : key;
}

const I18nContext = createContext({
  lang: currentLanguage,
  setLang: () => {},
  t: translate,
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(currentLanguage);

  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    currentLanguage = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // 저장소를 쓸 수 없는 환경
    }
    setLangState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translate('common.documentTitle');
  }, [lang]);

  // lang 이 바뀌면 t 도 새 함수가 되어, t 를 의존성으로 둔 useMemo/useCallback 이 다시 계산된다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const t = useCallback((key, params) => translate(key, params), [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
